import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import { CollectionListUrlSortField } from "@saleor/collections/urls";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import StatusLabel from "@saleor/components/StatusLabel";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { ListActions, ListProps, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { CollectionList_collections_edges_node } from "../../types/CollectionList";

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("lg")]: {
      colAvailability: {
        width: 240
      },
      colCoverageType: {
        width: 240
      },
      colName: {
        paddingLeft: 0
      },
      colProducts: {
        width: 240
      }
    },
    colAvailability: {
      textAlign: "center"
    },
    colCoverageType: {
      textAlign: "center"
    },
    colName: {
      width: "300px"
    },
    colProducts: {
      textAlign: "center"
    },
    collectionTbl: {
      "@media (max-width: 767px)": {
        width: "800px"
      }
    },
    tableRow: {
      cursor: "pointer" as "pointer"
    }
  }),
  { name: "CollectionList" }
);

interface CollectionListProps
  extends ListProps,
    ListActions,
    SortPage<CollectionListUrlSortField> {
  collections: CollectionList_collections_edges_node[];
}

const numberOfColumns = 5;

const CollectionList: React.FC<CollectionListProps> = props => {
  const {
    collections,
    disabled,
    settings,
    sort,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    onRowClick,
    onSort,
    pageInfo,
    isChecked,
    selected,
    toggle,
    toggleAll,
    toolbar
  } = props;

  const classes = useStyles(props);
  const intl = useIntl();

  function upperCaseToTitleCase(text: string) {
    return text
      .toLowerCase()
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  return (
    <ResponsiveTable className={classes.collectionTbl}>
      <TableHead
        colSpan={numberOfColumns}
        selected={selected}
        disabled={disabled || false}
        items={collections}
        toggleAll={toggleAll}
        toolbar={toolbar}
      >
        <TableCellHeader
          direction={
            sort.sort === CollectionListUrlSortField.name
              ? getArrowDirection(sort?.asc || false)
              : undefined
          }
          arrowPosition="right"
          onClick={() => onSort(CollectionListUrlSortField.name)}
          className={classes.colName}
        >
          <FormattedMessage defaultMessage="Collection Name" />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === CollectionListUrlSortField.coverageType
              ? getArrowDirection(sort?.asc || false)
              : undefined
          }
          textAlign="center"
          onClick={() => onSort(CollectionListUrlSortField.coverageType)}
          className={classes.colCoverageType}
        >
          <FormattedMessage defaultMessage="Collection Type" />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === CollectionListUrlSortField.productCount
              ? getArrowDirection(sort?.asc || false)
              : undefined
          }
          textAlign="center"
          onClick={() => onSort(CollectionListUrlSortField.productCount)}
          className={classes.colProducts}
        >
          <FormattedMessage defaultMessage="No. of Products" />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === CollectionListUrlSortField.available
              ? getArrowDirection(sort?.asc || false)
              : undefined
          }
          textAlign="center"
          onClick={() => onSort(CollectionListUrlSortField.available)}
          className={classes.colAvailability}
        >
          <FormattedMessage
            defaultMessage="Visibility Status"
            description="collection visibility status"
          />
        </TableCellHeader>
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns}
            settings={settings}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
      <TableBody>
        {renderCollection(
          collections,
          collection => {
            const isSelected = collection ? isChecked(collection.id) : false;
            return (
              <TableRow
                className={classes.tableRow}
                hover={!!collection}
                onClick={collection ? onRowClick(collection.id) : undefined}
                key={collection ? collection.id : "skeleton"}
                selected={isSelected}
                data-test="id"
                data-test-id={maybe(() => collection?.id)}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    disabled={disabled}
                    disableClickPropagation
                    onChange={() => toggle(collection?.id || "")}
                  />
                </TableCell>
                <TableCell className={classes.colName} data-test="name">
                  {maybe<React.ReactNode>(() => collection?.name, <Skeleton />)}
                </TableCell>
                <TableCell
                  className={classes.colCoverageType}
                  data-test="coverageType"
                >
                  {maybe<React.ReactNode>(
                    () => upperCaseToTitleCase(collection?.coverageType || ""),
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colProducts}>
                  {maybe<React.ReactNode>(
                    () => collection?.products?.totalCount,
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colAvailability}
                  data-test="published"
                  data-test-published={maybe(() => collection?.isPublished)}
                >
                  {maybe(
                    () => (
                      <StatusLabel
                        status={collection?.isPublished ? "success" : "error"}
                        label={
                          collection?.isPublished
                            ? intl.formatMessage({
                                defaultMessage: "Published",
                                description: "collection is published"
                              })
                            : intl.formatMessage({
                                defaultMessage: "Unpublished",
                                description: "collection is not published"
                              })
                        }
                      />
                    ),
                    <Skeleton />
                  )}
                </TableCell>
              </TableRow>
            );
          },
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No collections found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};

CollectionList.displayName = "CollectionList";
export default CollectionList;
